import React from 'react'
import {MdInfoOutline} from 'react-icons/md'
import AutoForm from '@components/AutoForm'
import Button from '@components/Button'
import Money from '@components/fields/Money'
import Phone from '@components/fields/Phone'
import RadioComponents from '@components/fields/RadioComponents'
import Stores from '@components/fields/Stores'
import NoSSR from '@components/NoSSR'
import SuspenseLoading from '@components/SuspenseLoading'
import useMessage from '@hooks/useMessage'
import useRef from '@hooks/useRef'
import useState from '@hooks/useState'
import Container from '@packages/justo-parts/lib/components/Container'
import Checkbox from '@packages/justo-parts/lib/components/fields/Checkbox'
import DateText from '@packages/justo-parts/lib/components/fields/DateText'
import Select from '@packages/justo-parts/lib/components/fields/Select'
import Text from '@packages/justo-parts/lib/components/fields/Text'
import Textarea from '@packages/justo-parts/lib/components/fields/Textarea'
import Tooltip from '@packages/justo-parts/lib/components/Tooltip'
import clipboard from '@public/images/complaints/clipboard.svg'
import user from '@public/images/complaints/user.svg'
import {Field, WithValue} from 'simple-react-form'

import Header from './Header'
import LegalInformation from './LegalInformation'
import Page from './Page'
import recaptcha from './recaptcha'
import Success from './Success'

import styles from './styles.module.css'

export default function Complaint() {
  const form = useRef(null)
  const showMessage = useMessage()
  const [doc, setDoc] = useState({amount: 0, storeId: null})
  const [secure, setSecure] = useState(false)
  const [sent, setSent] = useState(false)
  const [selectedStoreId, setSelectedStoreId] = useState()
  const [complaintCode, setComplaintCode] = useState()

  const DOCUMENT_TYPES = [
    {label: 'DNI', value: 'dni'},
    {label: 'Pasaporte', value: 'pasaporte'}
  ]
  const COMPLAINT_REASON = [
    {value: 'producto', children: <div className={styles.infoIcon}>Producto</div>},
    {value: 'servicio', children: <div className={styles.infoIcon}>Servicio</div>}
  ]
  const COMPLAINT_TYPE = [
    {
      value: 'reclamo',
      children: (
        <>
          Reclamo{' '}
          <Tooltip content="Disconformidad relacionada a los productos o servicios">
            <MdInfoOutline size={20} className={styles.infoIcon} color="#868686" />
          </Tooltip>
        </>
      )
    },
    {
      value: 'queja',
      children: (
        <>
          Queja{' '}
          <Tooltip content="Disconformidad no relacionada a los productos o servicios">
            <MdInfoOutline size={20} className={styles.infoIcon} color="#868686" />
          </Tooltip>
        </>
      )
    }
  ]

  const onSuccess = value => {
    showMessage('Reclamo guardado')
    setComplaintCode(value.code)
    setSent(true)
  }

  const onChange = changes => {
    setDoc({...changes, storeId: selectedStoreId})
  }

  return (
    <div className={styles.container}>
      <Page />
      <Container>
        {sent ? (
          <Success code={complaintCode} />
        ) : (
          <>
            <Header />
            <AutoForm
              mutation="saveComplaint"
              ref={form}
              doc={doc}
              onChange={onChange}
              onSuccess={onSuccess}>
              <div className={`row ${styles.marginSection}`}>
                <img className={styles.image} src={user} alt="user" />
                <p>Identificación del local</p>
              </div>
              <div className={styles.marginSection}>
                <div className={`row ${styles.marginRow}`}>
                  <div className="col-xs-12 col-sm-6">
                    <NoSSR>
                      <SuspenseLoading>
                        <Field
                          fieldName="storeId"
                          label="Seleccionar local"
                          type={Stores}
                          multi={false}
                          onChange={value => {
                            setSelectedStoreId(value)
                            setDoc({...doc, storeId: value})
                            return value
                          }}
                        />
                      </SuspenseLoading>
                    </NoSSR>
                  </div>
                  <div className="col-xs-12 col-sm-6">
                    <NoSSR>
                      <SuspenseLoading>
                        <LegalInformation storeId={selectedStoreId} />
                      </SuspenseLoading>
                    </NoSSR>
                  </div>
                </div>
              </div>
              <div className={`row ${styles.marginSection}`}>
                <img className={styles.image} src={user} alt="user" />
                <p>Identificación del Consumidor Reclamante</p>
              </div>
              <div className={styles.marginSection}>
                <div className={`row ${styles.marginRow}`}>
                  <div className="col-xs-12 col-sm-6">
                    <Field fieldName="fullName" type={Text} label="Nombre Completo" />
                  </div>
                  <div className="col-xs-12 col-sm-6">
                    <Field fieldName="address" type={Text} label="Domicilio" />
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 col-sm-6">
                    <div className="row">
                      <div className="col-xs-12 col-sm-6">
                        <Field
                          fieldName="documentType"
                          label="Documento Identidad"
                          placeholder="Selecciona un documento"
                          type={Select}
                          options={DOCUMENT_TYPES}
                        />
                      </div>
                      <div className="col-xs-12 col-sm-6">
                        <Field fieldName="documentNumber" type={Text} label="Número de documento" />
                      </div>
                    </div>
                  </div>
                  <div className="col-xs-12 col-sm-6">
                    <div className="row">
                      <div className="col-xs-12 col-sm-6">
                        <Field fieldName="email" label="Email" type={Text} />
                      </div>
                      <div className="col-xs-12 col-sm-6">
                        <Field fieldName="phone" type={Phone} label="Teléfono Fijo / Celular" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.marginSection}>
                <WithValue>
                  {value => (
                    <>
                      <Field fieldName="underAge" type={Checkbox} label="Soy menor de edad" />
                      {value.underAge ? (
                        <div className="row">
                          <div className="col-xs-12 col-sm-3">
                            <Field
                              fieldName="tutorName"
                              type={Text}
                              label="Nomber Padre/Madre/Tutor"
                            />
                          </div>
                          <div className="col-xs-12 col-sm-3">
                            <Field fieldName="tutorAddress" type={Text} label="Domicilio" />
                          </div>

                          <div className="col-xs-12 col-sm-3">
                            <Field fieldName="tutorEmail" type={Text} label="Correo electrónico" />
                          </div>
                          <div className="col-xs-12 col-sm-3">
                            <Field fieldName="tutorPhone" type={Phone} label="Teléfono" />
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </WithValue>
              </div>
              <div className={`row ${styles.marginSection} `}>
                <img
                  className={styles.image}
                  src={clipboard}
                  alt="Identificación del bien contratado"
                />
                <p>Identificación del bien contratado</p>
              </div>
              <div className={styles.marginSection}>
                <div className={`row ${styles.marginLeft} ${styles.marginRow}`}>
                  <Field
                    fieldName="complaintReason"
                    type={RadioComponents}
                    options={COMPLAINT_REASON}
                    className="row"
                  />
                </div>
                <div className="row">
                  <div className="col-xs-12 col-sm-4">
                    <Field
                      fieldName="amount"
                      type={Money}
                      value={doc.amount}
                      label="Monto Reclamado"
                    />
                  </div>
                  <div className="col-xs-12 col-sm-8">
                    <Field fieldName="description" type={Text} label="Descripción" />
                  </div>
                </div>
              </div>
              <div className={`row ${styles.marginSection}`}>
                <img className={styles.image} src={clipboard} alt="detail" />
                <p>Detalle de Reclamación y Pedido del Consumidor</p>
              </div>
              <div className={`row ${styles.marginSection}`}>
                <div className="col-xs-12 col-sm-6">
                  <Field
                    fieldName="issueDate"
                    label="Fecha del pedido"
                    type={DateText}
                    placeholder={'DD-MM-AAAA'}
                  />
                </div>
              </div>
              <div className={styles.marginSection}>
                <div className={`row ${styles.marginLeft} ${styles.marginRow}`}>
                  <Field
                    fieldName="complaintType"
                    type={RadioComponents}
                    options={COMPLAINT_TYPE}
                    className="row"
                  />
                </div>
                <div className="row">
                  <div className="col-xs-12 col-sm-6">
                    <Field
                      fieldName="detail"
                      type={Textarea}
                      label="Detalle"
                      style={{height: '200px'}}
                      autoResize={false}
                      placeholder="Detalles del reclamo o la queja."
                    />
                  </div>
                  <div className="col-xs-12 col-sm-6">
                    <Field
                      fieldName="request"
                      type={Textarea}
                      label="Información del Pedido"
                      style={{height: '200px'}}
                      autoResize={false}
                      placeholder="Detalles del pedido, número de pedido, plataforma, etc."
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <p>
                  * La formulación del reclamo no impide acudir a otras vías de solución de
                  controversias ni es requisito previo para interponer una denuncia ante el
                  INDECOPI.
                </p>
                <p>
                  * El proveedor deberá dar respuesta al reclamo en un plazo no mayor a quince (15)
                  días hábiles.
                </p>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <WithValue>
                    {value => {
                      return (
                        <Field
                          fieldName="token"
                          type={recaptcha}
                          onChange={v => {
                            !v ? setSecure(false) : setSecure(true)
                            value.token = v
                            return v
                          }}
                        />
                      )
                    }}
                  </WithValue>
                </div>
              </div>
            </AutoForm>
            <br />
            <Button disabled={!secure} onClick={() => secure && form.current.submit()}>
              Enviar
            </Button>
          </>
        )}
      </Container>
    </div>
  )
}
